/**
 * The session's Reference ID for display — shown WHENEVER one is stored, which in practice
 * is always.
 *
 * `Session.ReferenceId` is never empty: an admin's typed Session Id when there is one,
 * otherwise a dashed GUID the domain mints at creation (Session.cs). Both are shown. Every
 * path that stores a reference is listed in the table below, and none of them can leave it
 * blank, so the null return here is a guard for malformed data rather than a real state.
 *
 *   admin drawer, Session Id typed   → that label
 *   admin drawer, Session Id blank   → minted GUID
 *   applicant opens a copied link    → minted GUID (nothing appends ?ref=)
 *   link carrying ?ref=ORDER-88      → ORDER-88
 *   email invite, Session Id typed   → that label, shared by the whole batch
 *   email invite, Session Id blank   → GUID minted on the INVITE row, not the session
 *
 * An earlier version filtered the minted GUID out and reported "no reference", so those
 * rows read NA. That was reverted on request: the id is what the row actually has, and a
 * long value beats hiding the only identifier a support conversation can quote. The
 * consequence is that unlabelled runs display 36 characters — the fix for that is to type
 * a Session Id when sharing, not to blank the column.
 *
 * Kept as a helper rather than inlining `referenceId`, so the rule stays in one place if
 * this decision is revisited again.
 */
export function displayReferenceId(referenceId: string | null | undefined): string | null {
  return referenceId?.trim() || null;
}

/**
 * Placeholder spellings for "no verified name", both taken from the designer's own files
 * rather than chosen by us:
 *
 *   • `SessionDetailPage.tsx` seeds its session cards and tabs with `name: "N/A"`
 *   • `SessionsTable.tsx`     seeds its rows with `user: "NA"`
 *
 * So the difference is intentional in the design — the roomy cards and tabs carry the
 * slash, the dense table column does not — and it is worth keeping rather than
 * normalising, because the table's NA also has to sit beside the "NA" status pill two
 * columns over.
 */
export const NAME_PLACEHOLDER_CARD = 'N/A';
export const NAME_PLACEHOLDER_TABLE = 'NA';

/**
 * The applicant's name for display — the verified one, or the placeholder.
 *
 * Deliberately does NOT fall back to `applicantName`. That field is a creation-time label:
 * "Applicant" for every public run, or whatever an admin typed when starting one. Showing
 * it made three different people all read as "Applicant", which looks like real data and
 * is not. `applicantFullName` is stamped from the first step that actually verified a name,
 * so it is either the person's name or genuinely unknown.
 *
 * One helper because six places show this — the list's User column, the detail sidebar, the
 * open-session tabs, the Overview card, the review header and the delete dialogs — and they
 * must not disagree.
 *
 * Defaults to the card spelling; the table passes {@link NAME_PLACEHOLDER_TABLE} explicitly.
 */
export function displayApplicantName(
  applicantFullName: string | null | undefined,
  placeholder: string = NAME_PLACEHOLDER_CARD,
): string {
  const name = applicantFullName?.trim();
  return name ? toDisplayCase(name) : placeholder;
}

/**
 * Normalises a verified name for display: "BHALODI SOHAM HETALBHAI" and
 * "bhalodi soham hetalbhai" both become "Bhalodi Soham Hetalbhai".
 *
 * The providers disagree with each other about case. PAN returns the holder name in caps,
 * Aadhaar's sources vary, and OCR returns whatever it read off the card — so the same person
 * appears SHOUTING on one row and whispering on the next. None of that is information; it is
 * an artefact of which API answered.
 *
 * ONLY normalises when the value carries no case information of its own — all upper or all
 * lower. A name that already has mixed case was written that way deliberately, and
 * re-casing it would break the names that need it most: "McDonald" would become "Mcdonald",
 * "van der Berg" would become "Van Der Berg". Those are left exactly as received.
 *
 * Word boundaries include the separators that appear inside real names, not just spaces:
 * "MARY-JANE" → "Mary-Jane", "O'BRIEN" → "O'Brien", "K.M. NAIR" → "K.M. Nair". Capitalising
 * only after spaces would produce "Mary-jane" and "O'brien".
 *
 * Runs of whitespace are collapsed as well — a provider padding a field with double spaces
 * should not push the rest of a table row along.
 */
export function toDisplayCase(name: string): string {
  const collapsed = name.replace(/\s+/g, ' ').trim();

  const hasUpper = /\p{Lu}/u.test(collapsed);
  const hasLower = /\p{Ll}/u.test(collapsed);
  if (hasUpper && hasLower) {
    return collapsed;
  }

  // Unicode-aware: an Indian name may arrive in Devanagari, where the case concept does not
  // apply and every character must pass through untouched.
  return collapsed
    .toLocaleLowerCase()
    .replace(
      /(^|[\s\-'’.])(\p{L})/gu,
      (_match, boundary: string, letter: string) => boundary + letter.toLocaleUpperCase(),
    );
}
